import React, { useEffect, useState } from "react";
import {
  CalendarFeedStatus,
  calendarFeedURL,
  createCalendarFeed,
  getCalendarFeed,
  revokeCalendarFeed,
} from "../api";

// Settings → Calendar feed (#3063). The feed puts every watched property's sale
// date and upset-bid deadline on the user's own calendar. The link is a secret
// shown once: the server keeps only its hash, so "Get a new link" is the way
// back to a URL, and it retires the old one.

function formatDate(iso: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

const CalendarFeedSettings: React.FC = () => {
  const [status, setStatus] = useState<CalendarFeedStatus | null>(null);
  const [url, setUrl] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    getCalendarFeed()
      .then((s) => !cancelled && setStatus(s))
      .catch(() => !cancelled && setError("Couldn't load your calendar feed."));
    return () => {
      cancelled = true;
    };
  }, []);

  const create = async () => {
    setBusy(true);
    setError(null);
    setCopied(false);
    try {
      const { token, createdAt } = await createCalendarFeed();
      setUrl(calendarFeedURL(token));
      setStatus({ active: true, createdAt });
    } catch {
      setError("Couldn't create the link. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const revoke = async () => {
    setBusy(true);
    setError(null);
    try {
      await revokeCalendarFeed();
      setUrl(null);
      setStatus({ active: false, createdAt: null });
    } catch {
      setError("Couldn't turn the feed off. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const copy = async () => {
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };

  return (
    <>
      <h3>Calendar feed</h3>
      <p className="settings-section-note">
        Put your watched properties' sale dates and upset-bid deadlines on Google, Apple or Outlook
        Calendar, with a reminder the day before. Follow-up dates from your contact log show up too, until you mark them done. Your calendar app checks for changes every few hours, so
        postponed sales move on their own.
      </p>

      {error && <p className="settings-section-note calendar-feed-error" role="alert">{error}</p>}

      {url && (
        <div className="calendar-feed-link">
          <div className="calendar-feed-url">
            <input readOnly value={url} aria-label="Calendar feed link" onFocus={(e) => e.target.select()} />
            <button type="button" className="settings-btn" onClick={copy}>
              {copied ? "Copied" : "Copy link"}
            </button>
          </div>
          <ul className="settings-section-note calendar-feed-steps">
            <li>
              <strong>Google Calendar:</strong> Other calendars → + → From URL, then paste the link.
            </li>
            <li>
              <strong>Apple or Outlook:</strong>{" "}
              <a href={url.replace(/^https?:\/\//i, "webcal://")}>open it in your calendar app</a>.
            </li>
          </ul>
          <p className="settings-section-note">
            Keep this link private: anyone who has it can see your watched properties' dates. This is the
            only time it's shown.
          </p>
        </div>
      )}

      {status?.active && !url && (
        <p className="settings-section-note">
          Your feed is on{status.createdAt ? ` (link created ${formatDate(status.createdAt)})` : ""}. The link is
          only shown once; get a new one to copy it again. The old link stops working.
        </p>
      )}

      {status && (
        <div className="calendar-feed-actions">
          <button type="button" className="settings-btn" onClick={create} disabled={busy}>
            {status.active ? "Get a new link" : "Create calendar link"}
          </button>
          {status.active && (
            <button type="button" className="settings-link-btn" onClick={revoke} disabled={busy}>
              Turn off
            </button>
          )}
        </div>
      )}
    </>
  );
};

export default CalendarFeedSettings;
